import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, CreditCard, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import PageHeader from '../components/shared/PageHeader';
import PlanBrand, { isSynesPlan } from '../components/shared/PlanBrand';
import gdnLogo from '../assets/gdn-logo.jpeg';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { currency } from '../utils/formatters';
import { normalizeMoney } from '../utils/masks';

function formatLimit(value, label) {
  return value === null || value === undefined ? `${label} ilimitados` : `${value} ${label}`;
}

function markUnavailable(plan, item) {
  return item;
}

function premiumSynesItems(plan) {
  if (!isSynesPlan(plan)) return [];
  return [
    'WhatsApp automatizado por canais oficiais META',
    'CRM completo com funil, contatos, tarefas e catálogo',
    'Triagem e qualificação de leads com IA 24/7',
    'Réguas de relacionamento e conversão via webhooks',
    'Entrega Synes.ia dentro do MecSync Premium',
  ];
}

function planPositioning(plan) {
  if (plan.codigo === 'pro') {
    return 'Melhor custo-benefício: por pouco a mais que o Starter, libera check-in, orçamento por link, timeline e oportunidades que podem pagar a diferença.';
  }
  if (isSynesPlan(plan)) {
    return 'Entrega premium para escalar atendimento: IA, WhatsApp oficial, CRM, funil e retorno comercial com implantação assistida.';
  }
  return 'Entrada justa para sair do papel: OS, clientes, veículos, estoque e financeiro simples sem comprometer caixa.';
}

export default function Planos() {
  const location = useLocation();
  const navigate = useNavigate();
  const { perfil } = useAuth();
  const isGdnPanel = location.pathname.startsWith('/gdn');
  const canManagePlans = isGdnPanel && ['gdn_admin', 'super_admin'].includes(String(perfil?.perfil || ''));
  const [plans, setPlans] = useState([]);
  const [planPrices, setPlanPrices] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingPlanId, setSavingPlanId] = useState(null);
  const [choosingPlanId, setChoosingPlanId] = useState(null);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    const [plansRes, pricesRes] = await Promise.all([
      supabase
        .from('planos_mecsync')
        .select('*')
        .eq('ativo', true)
        .order('valor_mensal', { ascending: true }),
      supabase
        .from('mecsync_plan_prices')
        .select('*')
        .eq('active', true),
    ]);

    const { data, error } = plansRes;

    if (error) {
      toast.error('Não foi possível carregar os planos');
      setLoading(false);
      return;
    }

    if (pricesRes.error) {
      toast.error('NÃ£o foi possÃ­vel carregar os valores por moeda');
      setLoading(false);
      return;
    }

    const loadedPlans = data || [];
    setPlans(loadedPlans);
    setPlanPrices(pricesRes.data || []);

    if (!isGdnPanel && perfil?.oficina_id) {
      const { data: assinatura, error: subscriptionError } = await supabase
        .from('assinaturas')
        .select('*')
        .eq('oficina_id', perfil.oficina_id)
        .maybeSingle();

      if (subscriptionError) {
        toast.error('Não foi possível carregar sua assinatura atual.');
      } else {
        setSubscription(assinatura || null);
      }
    }

    setDrafts(Object.fromEntries(loadedPlans.map((plan) => [
      plan.id,
      {
        valor_mensal: String(plan.valor_mensal ?? ''),
        applyToSubscriptions: false,
      },
    ])));
    setLoading(false);
  }, [isGdnPanel, perfil?.oficina_id]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const normalizedPlans = useMemo(() => (
    plans.map((plan) => ({
      ...plan,
      items: Array.from(new Set([
        ...(plan.beneficios || []),
        ...premiumSynesItems(plan),
        formatLimit(plan.limite_usuarios, 'usuário(s)'),
        formatLimit(plan.limite_os_mes, 'OS/mês'),
        formatLimit(plan.limite_agendamentos_mes, 'agendamentos/mês'),
        plan.relatorios_avancados ? 'Relatórios avançados' : null,
        plan.ia_clientes ? 'IA respondendo clientes em parceria com Synes.ia' : null,
      ].filter(Boolean))).map((item) => markUnavailable(plan, item)),
    }))
  ), [plans]);

  function updateDraft(planId, patch) {
    setDrafts((prev) => ({
      ...prev,
      [planId]: {
        ...(prev[planId] || {}),
        ...patch,
      },
    }));
  }

  function planMonthlyValue(plan) {
    const currencyCode = String(subscription?.moeda || 'BRL').toUpperCase();
    const price = planPrices.find((item) => (
      String(item.plan_code || '').toLowerCase() === String(plan.codigo || '').toLowerCase()
      && String(item.currency || '').toUpperCase() === currencyCode
    ));
    return Number(price?.monthly_value ?? plan.valor_mensal ?? 0);
  }

  function planCurrency(plan) {
    const currencyCode = String(subscription?.moeda || 'BRL').toUpperCase();
    return currency(planMonthlyValue(plan), {
      currency: currencyCode,
      locale: currencyCode === 'BRL' ? 'pt-BR' : 'en-US',
    });
  }

  async function savePlanPrice(plan) {
    const draft = drafts[plan.id] || {};
    const nextValue = Number(String(draft.valor_mensal || '').replace(',', '.'));

    if (!Number.isFinite(nextValue) || nextValue < 0) {
      toast.error('Informe um valor mensal válido para o plano.');
      return;
    }

    setSavingPlanId(plan.id);
    try {
      const { error: planError } = await supabase
        .from('planos_mecsync')
        .update({ valor_mensal: Number(nextValue.toFixed(2)) })
        .eq('id', plan.id);

      if (planError) throw planError;

      if (draft.applyToSubscriptions) {
        const { error: subscriptionError } = await supabase
          .from('assinaturas')
          .update({ valor_mensal: Number(nextValue.toFixed(2)) })
          .eq('plano_id', plan.id)
          .neq('status_pagamento', 'cancelado');

        if (subscriptionError) throw subscriptionError;
      }

      toast.success(draft.applyToSubscriptions
        ? 'Valor do plano e assinaturas existentes atualizados.'
        : 'Valor padrão do plano atualizado.');
      await loadPlans();
    } catch (error) {
      console.error('Erro ao atualizar plano:', error);
      toast.error(error.message || 'Não foi possível atualizar o valor do plano.');
    } finally {
      setSavingPlanId(null);
    }
  }

  async function choosePlan(plan) {
    if (!subscription?.id) {
      toast.error('Assinatura não encontrada para esta oficina.');
      return;
    }

    setChoosingPlanId(plan.id);
    try {
      const { data, error } = await supabase.functions.invoke('select-subscription-plan', {
        body: { plan_id: plan.id },
      });

      if (error || data?.error) {
        throw new Error(data?.message || data?.error || error?.message || 'Não foi possível escolher o plano.');
      }

      toast.success(data?.current_plan_selected
        ? 'Plano atual confirmado. Vamos para o pagamento.'
        : `Plano ${plan.nome} escolhido. Agora é só finalizar o pagamento.`);
      navigate(`/configuracoes/pagamento?plano=${encodeURIComponent(plan.codigo || plan.nome || '')}`);
    } catch (error) {
      console.error('Erro ao escolher plano:', error);
      toast.error(error.message || 'Não foi possível escolher o plano.');
    } finally {
      setChoosingPlanId(null);
    }
  }

  function getPlanState(plan) {
    const current = String(subscription?.plano_id || '') === String(plan.id)
      || String(subscription?.plano || '').trim().toLowerCase() === String(plan.nome || '').trim().toLowerCase();
    const pending = String(subscription?.pending_plan_id || '') === String(plan.id)
      || String(subscription?.pending_plan_code || '') === String(plan.codigo || '');
    return { current, pending };
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Planos MecSync"
        subtitle="Benefícios e limites disponíveis para os clientes"
        actions={<Button variant="ghost" onClick={loadPlans} disabled={loading}><RefreshCw size={15} /> Atualizar</Button>}
      />

      <div className="grid gap-3 xl:grid-cols-3">
        {normalizedPlans.map((plan) => (
          <Card key={plan.id} className={isSynesPlan(plan) ? 'border-yellow-400/50' : plan.codigo === 'pro' ? 'border-accent' : ''}>
            <div className="mb-5 flex items-start justify-between gap-3">
              <div className="min-w-0">
                {isSynesPlan(plan) ? <PlanBrand plan={plan} compact /> : <h3 className="font-display text-2xl">{plan.nome}</h3>}
                <p className="mt-1 text-sm text-zinc-400">{planPositioning(plan)}</p>
                <p className="mt-4 font-display text-3xl font-black text-white">
                  {planCurrency(plan)}
                  <span className="ml-1 font-sans text-sm font-medium text-zinc-400">/ mês</span>
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                {isSynesPlan(plan) ? <Badge className="bg-yellow-400/10 text-yellow-300">Premium IA</Badge> : plan.codigo === 'pro' && <Badge className="bg-orange-500 text-black">Recomendado</Badge>}
                {!isGdnPanel && getPlanState(plan).current && <Badge>Plano atual</Badge>}
                {!isGdnPanel && getPlanState(plan).pending && <Badge className="bg-blue-500/15 text-blue-300">Selecionado</Badge>}
              </div>
            </div>
            {plan.codigo === 'pro' && (
              <div className="mb-4 rounded-lg border border-orange-400/30 bg-orange-500/10 p-3 text-sm leading-6 text-orange-100">
                O Pro é o plano mais equilibrado: uma aprovação recuperada no mês já tende a pagar a diferença para o Starter.
              </div>
            )}
            {isSynesPlan(plan) && (
              <div className="mb-4 rounded-lg border border-yellow-400/20 bg-yellow-400/10 p-3 text-sm leading-6 text-yellow-100">
                Automação comercial, WhatsApp oficial e CRM são implantados pela Synes.ia como parte da entrega Premium do MecSync.
              </div>
            )}
            <ul className="space-y-3 text-sm text-zinc-300">
              {plan.items.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0 text-orange-400" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            {!isGdnPanel && (
              <Button
                className="mt-6 w-full justify-center"
                onClick={() => choosePlan(plan)}
                disabled={choosingPlanId === plan.id || loading}
              >
                <CreditCard size={16} />
                {choosingPlanId === plan.id
                  ? 'Selecionando...'
                  : getPlanState(plan).current
                    ? 'Pagar ou renovar este plano'
                    : getPlanState(plan).pending
                      ? 'Continuar para pagamento'
                      : `Escolher ${plan.nome}`}
              </Button>
            )}
            {canManagePlans && (
              <div className="mt-6 rounded-lg border border-border bg-surface2 p-3">
                <h4 className="font-semibold text-white">Alterar valor do plano</h4>
                <p className="mt-1 text-xs text-zinc-400">
                  Esse valor vira o padrão para novos clientes. Marque a opção abaixo para atualizar também assinaturas existentes desse plano.
                </p>
                <div className="mt-3 grid gap-2">
                  <Input
                    placeholder="Valor mensal"
                    mask="money"
                    value={drafts[plan.id]?.valor_mensal ?? ''}
                    onChange={(event) => updateDraft(plan.id, { valor_mensal: normalizeMoney(event.target.value) })}
                  />
                  <label className="flex items-start gap-2 text-xs text-zinc-300">
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-orange-500"
                      checked={Boolean(drafts[plan.id]?.applyToSubscriptions)}
                      onChange={(event) => updateDraft(plan.id, { applyToSubscriptions: event.target.checked })}
                    />
                    <span>Aplicar também aos clientes ativos vinculados a este plano</span>
                  </label>
                  <Button
                    className="justify-center"
                    onClick={() => savePlanPrice(plan)}
                    disabled={savingPlanId === plan.id}
                  >
                    {savingPlanId === plan.id ? 'Salvando...' : 'Salvar valor'}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>

      {!loading && normalizedPlans.length === 0 && (
        <Card>
          <p className="text-sm text-zinc-400">Nenhum plano ativo encontrado.</p>
        </Card>
      )}

      <Card className="flex flex-wrap items-center gap-3">
        <img src={gdnLogo} alt="GDN Corp" className="h-12 w-auto rounded bg-[#111113] object-contain p-1" />
        <img src="/synes-ia-logo-transparent.png" alt="Synes.ia" className="h-12 w-auto rounded object-contain p-1 drop-shadow-lg" />
        <p className="text-sm text-zinc-300">
          Planos, ativações e suporte comercial são atendidos pela GDN Corp. No Premium, a camada de WhatsApp automatizado e CRM tem entrega parceira Synes.ia.
        </p>
      </Card>
    </div>
  );
}
